<template>
  <!-- ClientOnly 避免旧内核 Teleport hydration mismatch -->
  <ClientOnly>
    <Teleport to="body">
      <div v-if="open" class="ws-sheet">
        <div class="ws-mask" @click="close" />
        <div class="ws-body">
          <div class="ws-title">
            {{ t('me.tfSelectVenue') }}
            <button type="button" class="ws-x" :aria-label="t('common.close')" @click="close">
              ×
            </button>
          </div>

          <div class="vs-search">
            <span aria-hidden="true">🔍</span>
            <input
              v-model="kw"
              type="text"
              inputmode="search"
              enterkeyhint="search"
              autocomplete="off"
              :placeholder="t('me.tfSearchVenue')"
            />
          </div>

          <div class="chips vs-cats">
            <button
              v-for="c in cats"
              :key="c.key || 'all'"
              type="button"
              class="chip"
              :class="{ on: cat === c.key }"
              @click="cat = c.key"
            >
              {{ c.label }}
            </button>
          </div>

          <div class="vs-list">
            <button
              v-for="v in list"
              :key="v.k"
              type="button"
              class="vs-row"
              :class="{ on: v.k === selected, 'vs-dim': v.mnt || v.pend }"
              @click="emit('pick', v.k)"
            >
              <div class="vr-l">
                <b>{{ v.n }}</b>
                <span>
                  <template v-if="catLabel(v.cat)">{{ catLabel(v.cat) }}</template>
                  <em v-if="v.mnt" class="tfv-tag mnt">{{ t('me.tfMaint') }}</em>
                  <em v-else-if="v.pend" class="tfv-tag pend">{{ t('me.tfPending') }}</em>
                </span>
              </div>
              <div class="vr-r">
                <b :class="{ pos: v.balKnown && v.bal > 0 }">
                  {{ formatVenueAmt(v) }}
                </b>
                <i v-if="v.k === selected" class="vr-on">✓</i>
              </div>
            </button>
            <div v-if="loading && !list.length" class="vs-empty">{{ t('me.tfLoading') }}</div>
            <div v-else-if="!list.length" class="vs-empty">
              {{ t('me.tfEmptyVenue') }}
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import type { TransferVenue, VenueCat } from '~/utils/transfer/types'

const props = defineProps<{
  modelValue: boolean
  selected: string
  cat: VenueCat
  cats: { key: VenueCat; label: string }[]
  venues: TransferVenue[]
  loading?: boolean
  catLabel: (cat: VenueCat) => string
  formatVenueAmt: (v: TransferVenue) => string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'update:cat': [value: VenueCat]
  pick: [key: string]
}>()

const { t } = useI18n()

const open = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

const cat = computed({
  get: () => props.cat,
  set: (v: VenueCat) => emit('update:cat', v),
})

const kw = ref('')

watch(
  () => props.modelValue,
  v => {
    if (v) {
      cat.value = ''
      kw.value = ''
    }
  },
)

const list = computed(() => {
  const q = kw.value.trim().toLowerCase()
  if (!q) return props.venues
  return props.venues.filter(v => v.n.toLowerCase().includes(q))
})

const close = () => {
  open.value = false
}
</script>

<style scoped lang="scss">
.ws-sheet {
  position: fixed;
  /* Chrome < 87 无 inset，需显式四边（否则旧 WebView 只露出左侧一条） */
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 90;
}

.ws-mask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(5, 5, 12, 0.6);
}

.ws-body {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  max-width: 480px;
  margin: 0 auto;
  background: var(--surface);
  border-radius: 18px 18px 0 0;
  padding: 14px 14px calc(24px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--hairline);
  max-height: 78vh;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

/* 仅 flex，字号随正文 14px，不用 900 加粗 */
.ws-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  line-height: 20px;
  min-height: 20px;
}

/* 轻量关闭符，靠右 */
.ws-x {
  margin-left: auto;
  background: none;
  border: 0;
  color: var(--text);
  font-size: 22px;
  font-weight: 300;
  line-height: 20px;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:active {
    opacity: 0.65;
  }
}

.vs-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface2);
  border: 1px solid var(--hairline);
  border-radius: 999px;
  padding: 9px 14px;
  margin: 4px 0 10px;
  box-sizing: border-box;

  > span {
    font-size: 14px;
    line-height: 1;
    flex: 0 0 auto;
  }

  input {
    flex: 1;
    background: none;
    border: 0;
    outline: 0;
    color: var(--text);
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    min-width: 0;
    padding: 0;
    margin: 0;
    -webkit-appearance: none;
    appearance: none;

    /* placeholder 随 input 色降透明，勿用 --muted */
    &::placeholder {
      color: var(--text);
      opacity: 0.45;
      font-weight: 400;
    }
  }
}

.chips {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 0 0 8px;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.chip {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: var(--r-xl);
  font-size: 13px;
  font-weight: 700;
  background: var(--surface);
  border: 1px solid var(--hairline);
  color: var(--muted);

  &.on {
    background: var(--primary);
    color: #fff;
    border-color: transparent;
  }
}

.vs-list {
  max-height: 48vh;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.vs-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 2px;
  border: 0;
  border-bottom: 1px solid var(--hairline);
  background: none;
  color: var(--text);
  text-align: left;

  &:last-child {
    border-bottom: 0;
  }

  &.vs-dim {
    opacity: 0.5;
  }

  &:active {
    opacity: 0.75;
  }
}

.vr-l {
  min-width: 0;

  b {
    font-size: 14px;
    font-weight: 800;
    display: block;
  }

  span {
    font-size: 11px;
    color: var(--muted);
    margin-top: 3px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.tfv-tag {
  font-style: normal;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;
  color: #fff;

  &.mnt {
    background: #5a6478;
  }

  &.pend {
    background: #c48a3a;
  }
}

.vr-r {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;

  b {
    font-size: 13px;
    font-weight: 700;
    color: var(--muted);

    &.pos {
      color: var(--success);
    }
  }
}

.vr-on {
  font-style: normal;
  color: var(--primary);
  font-weight: 800;
}

.vs-empty {
  padding: 28px 12px;
  text-align: center;
  font-size: 13px;
  color: var(--muted);
}
</style>
